/**
 * `DesktopFeeOverdueMarker` — the unpaid-fee marker on Omat tiedot in the
 * desktop sidebar (058 FR-014, contract desktop-sidebar §3).
 *
 * It shows exactly when the 057 server decision says the Home overdue-fee
 * notice shows, read from the same cached billing read, so the two can never
 * disagree. The prototype shows it always; a debt marker must not be guessed
 * (P7, D-PD-023). Members only; a failed, pending or unavailable read renders
 * nothing and costs the rest of the sidebar nothing.
 *
 * The shape is the prototype's (a filled circle with an exclamation mark, on a
 * 32-unit grid), coloured from the destructive tokens rather than its raw hex.
 */

import { type JSX, Suspense } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';
import { useMembershipBilling } from '../membership-billing/useMembershipBilling.js';
import { useSession } from '../session/useSession.js';

import { PortErrorBoundary } from './PortErrorBoundary.js';

function OverdueMarker(): JSX.Element | null {
  const t = useTranslate();
  const { billingFacts } = useMembershipBilling();
  if (billingFacts.overdueNotice !== 'show') return null;
  return (
    <span className="inline-flex shrink-0">
      <svg viewBox="0 0 32 32" aria-hidden="true" className="h-stock-5 w-stock-5">
        <circle cx="16" cy="16" r="15" className="fill-destructive" />
        <rect
          x="14.25"
          y="7.5"
          width="3.5"
          height="11"
          rx="1.75"
          className="fill-destructive-foreground"
        />
        <circle cx="16" cy="23" r="2" className="fill-destructive-foreground" />
      </svg>
      <span className="sr-only">{t('shell.desktopSidebar.feeOverdueMarker')}</span>
    </span>
  );
}

export function DesktopFeeOverdueMarker(): JSX.Element | null {
  const { state } = useSession();
  if (state.kind !== 'member') return null;
  return (
    <PortErrorBoundary portName="membershipBilling" fallback={null}>
      <Suspense fallback={null}>
        <OverdueMarker />
      </Suspense>
    </PortErrorBoundary>
  );
}
